'use client'
import type { FC, MouseEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styled, { css, type RuleSet } from 'styled-components'
import { motion, AnimatePresence, type Variants } from 'motion/react'
import { slugify, getIcon } from '@/utils'
import { Img } from '@/components'
import { mq, vw, designGrid, defaultGrid } from '@/styles'
import { useMount } from '@/hooks'

type xAlign = 'flex-start' | 'center' | 'flex-end' | 'right'
type size = 'full' | 'auto' | 's' | 'm' | 'l'
export interface iModal {
  title?: string,
  children: ReactNode,
  isActive: boolean,
  hasHeader?: boolean,
  keepGrid?: boolean,
  hasToClose?: boolean,
  zIndex?: number,
  readonly close: () => void,
  readonly xAlign?: xAlign,
  readonly variants?: (isActive?: boolean) => Variants
  readonly dialogStyles?: RuleSet<object>
  readonly onClickBack?: () => void
  readonly size?: size
}

const backIcon = {
  type: 'svg' as const,
  src: '/images/svg/i--arrow.svg',
  alt: 'Go back icon',
  size: { width: 12, height: 12 }
}

export const Modal:FC<iModal> = ({ title = '', children, isActive, close, xAlign = 'center', variants = defaultDialogVariants, dialogStyles = null, onClickBack = null, size = 'auto', hasHeader = true, zIndex = 600, keepGrid = false, hasToClose = false }) => {
  const titleAsSlug = slugify(title)
  const isMounted = useMount()

  const onClick = (e:MouseEvent) => {
    if((e.target as HTMLElement).getAttribute('tabindex') === '-1') close()
  }

  return (isMounted ? createPortal(
    <AnimatePresence>
      <ModalStyled tabIndex={-1} aria-hidden={!isActive} initial={{ opacity: 0, zIndex:1 }} animate={{ opacity: isActive ? 1 : 0, zIndex: isActive ? zIndex : 1 }} exit={{ opacity: 0, zIndex: 1 }} transition={ { opacity: { duration: 0.3} ,  zIndex: { duration: 0.8}}} onClick={onClick} $xAlign={xAlign} $zIndex={zIndex} $keepGrid={keepGrid}>
        <DialogStyled $size={size} role='dialog' aria-modal='true' aria-labelledby={titleAsSlug} {...variants(isActive)} {...(dialogStyles && { $dialogStyles: dialogStyles })} $xAlign={xAlign} $isActive={isActive}>
          {hasHeader &&
          <header>
            <>
              {onClickBack && <ButtonHeader className='btn--back' type='button' onClick={onClickBack}>
                <Img media={backIcon} fill={false} />
              </ButtonHeader>}
              <h2 id={titleAsSlug}>{title}</h2>
              {hasToClose &&
                <ButtonHeader className='btn--close' type='button' onClick={close}>
                  <Img media={getIcon('close')} fill={false} />
                </ButtonHeader>
              }
            </>
          </header>
          }
          <div className='modal__body'>
            {children}
          </div>
        </DialogStyled>
      </ModalStyled>
    </AnimatePresence>, document.body) : null
  )
}


const ModalStyled = styled(motion.aside) <{ $xAlign: xAlign, $zIndex, $keepGrid: boolean }>`
  ${designGrid({})}
  align-items: flex-start;
  background-color: rgba(0, 0, 0, .2);
  bottom: 0;
  display: flex;
  height: 100vh;
  height: 100dvh;
  justify-content: ${({ $xAlign }) => $xAlign};
  left: 0;
  max-width: 100vw;
  max-width: 100dvw;
  opacity: 0;
	outline: 0;
  overflow-y: auto;
	overscroll-behavior: contain;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  user-select: none;
  width: 100%;
  will-change: opacity, z-index;

  ${mq.greaterThan('tablet')} {
    display: ${({ $keepGrid }) => $keepGrid ? 'grid' : 'flex'};
  }

  &[aria-hidden='false'] {
    opacity: 1;
    pointer-events: auto;
    user-select: auto;
  }

  .modal__body {
    position: relative;
    width: 100%;
    height: unset;
  }
  /* MOBILE LANDSCAPE */
  @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: landscape) {
    width: 100%;

    .modal__body {
      height: unset;
    }
  }
`

export const ButtonHeader = styled.button`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 100%;
  justify-content: center;
  margin-left: auto;
  max-width: 48px;
  width: ${vw(48, 'mobile')};
  z-index: 2;

  ${mq.greaterThan('nexus7')} {
    width: ${vw(48, 'nexus7')};
  }

  ${mq.greaterThan('tablet')} {
    width: ${vw(48, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    height: 48px;
    width: 48px;
  }

  figure, img {
    max-height: 35px;
    max-width: 35px;
    width: 35px;
    height: 35px;
  }

  .cross, .cross > img {
    max-height: 21px;
    max-width: 21px;
    width: 21px;
    height: 21px;
    ${mq.greaterThan('nexus7')} {
      width: ${vw(21, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      width: ${vw(21, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      width: 21px;
    }
  }

  &.btn--back {
    left: 0;
    position: absolute;
  }
`

const maxWidthBySize = {
  full: '100%',
  auto: 'unset', // default
  s: 550,
  m: 750,
  l: 900
}

const DialogStyled = styled(motion.div)<{ $xAlign: xAlign, $isActive: boolean, $size: size, $hasBackButton?: boolean, $dialogStyles?: iModal['dialogStyles'] }>`
  background-color: var(--color-modal-background);
  box-shadow: 0 25px 50px rgba(0, 0, 0, .15);
  grid-column: 1 / span 6;
  margin: ${({ $xAlign }) => $xAlign === 'right' ? '0px 0px 0px auto' : `70px auto ${vw(200, 'mobile')}`};
  max-width: ${({ $size }) => typeof maxWidthBySize[$size] === 'string' ? maxWidthBySize[$size] : vw(maxWidthBySize[$size] as number, 'mobile')};
  position: relative;
  width: 100%;
  ${({ $xAlign }) => $xAlign === 'right' && 'height: 100vh;'}
  ${({ $xAlign }) => $xAlign === 'right' && 'height: 100dvh;'}
  ${({ $isActive }) => !$isActive && css`
    user-select: none;
    pointer-events: none;

    > *,
    li, dd,
    button, a, form, fieldset, input, select, label,
    figure, div {
      user-select: none !important;
      pointer-events: none !important;
    }
  `}

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
    margin: ${({ $xAlign }) => $xAlign === 'right' ? '0 0 0 auto' : `70px auto ${vw(200, 'desktop')}`};
    width: calc(100% - ${defaultGrid.tablet.gutter.width}px);
    max-width: ${({ $size }) => typeof maxWidthBySize[$size] === 'string' ? maxWidthBySize[$size] : vw(maxWidthBySize[$size] as number, 'desktop')};
  }

  ${mq.greaterThan('desktop-xs')} {
    margin: ${({ $xAlign }) => $xAlign === 'right' ? '0 0 0 auto' : '90px auto 200px'};
  }

  ${mq.greaterThan('desktop')} {
    width: calc(100% - ${defaultGrid.tablet.gutter.width}px);
    max-width: ${({ $size }) => typeof maxWidthBySize[$size] === 'string' ? maxWidthBySize[$size] : `${maxWidthBySize[$size]}px`};
  }

  > header {
    align-items: center;
    display: flex;
    height: 65px;
    justify-content: center;
    overflow-x: hidden;
    padding-left: ${vw(48, 'mobile')};
    position: relative;
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      padding-left: ${vw(48, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      padding-left: ${vw(48, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 50px;
      padding-left: 48px;
    }

    h2 {
      color: inherit;
      font-size: 20px;
      font-weight: 500;
      margin-bottom: 0;
      width: 100%;

      ${mq.greaterThan('tablet')} {
        font-weight: 400;
      }
    }
  }

  .modal__body {
    padding: ${vw(50, 'mobile')} ${vw(40 - 4.25, 'mobile')} ${vw(75, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(50, 'nexus7')} ${vw(40 - 4.25, 'nexus7')} ${vw(75, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(50, 'desktop')} ${vw(40 - 12.5, 'desktop')} ${vw(75, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 50px 27.5px 75px;
    }
  }

  @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: landscape) {
    margin: ${({ $xAlign }) => $xAlign === 'right' ? '0px 0px 0px auto' : '40px auto'};
    height: max-content;

    > header {
      height: ${vw(30, 'mobile')};
    }

    .modal__body {
      padding: ${vw(20, 'mobile')} ${vw(40 - 4.25, 'mobile')} ${vw(75, 'mobile')};
      min-height: 100%;
    }
  }

  ${({ $dialogStyles }) => $dialogStyles && $dialogStyles}
`

const transition = { transition: { duration: .3, delay: .1 } }
const defaultDialogVariants = (isActive: boolean): Variants => ({
  initial: { y: '10px', ...transition },
  animate: { y: isActive ? 0 : '10px', ...transition },
  exit: { y: '10px', ...transition },
})
